import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notificationsApi, Notification } from '@/api/notifications';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, Bell, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const TYPE_BADGES: Record<string, { variant: 'default' | 'success' | 'warning' | 'destructive'; label: string }> = {
  news: { variant: 'default', label: 'Nouveaute' },
  trick: { variant: 'success', label: 'Astuce' },
  warning: { variant: 'warning', label: 'Alerte' },
};

export function NotificationsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [deleteNotification, setDeleteNotification] = useState<Notification | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');
  const isSuperAdmin = hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationsApi.getAll();
      setNotifications(data);
    } catch (err) {
      setError('Erreur lors du chargement des notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async () => {
    if (!deleteNotification) return;

    try {
      setDeleting(true);
      await notificationsApi.delete(deleteNotification.id);
      setNotifications(notifications.filter((n) => n.id !== deleteNotification.id));
      setDeleteNotification(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de la notification');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedNotifications = useMemo(() => {
    let result = [...notifications];

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter((n) => n.type === typeFilter);
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter(
        (n) =>
          n.name.toLowerCase().includes(search) ||
          n.message.toLowerCase().includes(search) ||
          n.page.toLowerCase().includes(search)
      );
    }

    // Sort
    result.sort((a, b) => {
      const aValue = a[sortBy as keyof Notification];
      const bValue = b[sortBy as keyof Notification];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return result;
  }, [notifications, searchValue, typeFilter, sortBy, sortOrder]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isActive = (notification: Notification) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = notification.startingDate ? new Date(notification.startingDate) : null;
    const endDate = new Date(notification.endingDate);

    if (startDate) {
      startDate.setHours(0, 0, 0, 0);
    }
    endDate.setHours(0, 0, 0, 0);

    const hasStarted = !startDate || startDate <= today;
    const hasNotEnded = endDate >= today;

    return hasStarted && hasNotEnded;
  };

  const columns: Column<Notification>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      className: 'w-16',
      render: (notification) => (
        <span className="font-mono text-sm text-gray-500">{notification.id}</span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      className: 'w-28',
      render: (notification) => {
        const badge = TYPE_BADGES[notification.type] || { variant: 'default', label: notification.type };
        return <Badge variant={badge.variant}>{badge.label}</Badge>;
      },
    },
    {
      key: 'name',
      header: 'Titre',
      sortable: true,
      render: (notification) => (
        <div>
          <span className="font-medium text-gray-900">{notification.name}</span>
          {isActive(notification) && (
            <Badge variant="success" className="ml-2 text-xs">
              Active
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'page',
      header: 'Page',
      sortable: true,
      render: (notification) => (
        <span className="text-gray-600">{notification.page}</span>
      ),
    },
    {
      key: 'url',
      header: 'Lien',
      className: 'w-20',
      render: (notification) =>
        notification.url ? (
          <a
            href={notification.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'startingDate',
      header: 'Debut',
      sortable: true,
      className: 'w-28',
      render: (notification) => (
        <span className="text-gray-600">{formatDate(notification.startingDate)}</span>
      ),
    },
    {
      key: 'endingDate',
      header: 'Fin',
      sortable: true,
      className: 'w-28',
      render: (notification) => (
        <span className="text-gray-600">{formatDate(notification.endingDate)}</span>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (notification) => (
        <div className="flex flex-wrap gap-1">
          {notification.roles.slice(0, 2).map((role) => (
            <Badge key={role.id} variant="outline" className="text-xs">
              {role.name}
            </Badge>
          ))}
          {notification.roles.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{notification.roles.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right w-24',
      render: (notification) => (
        <div className="flex items-center justify-end gap-2">
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate(`/notifications/${notification.id}/edit`)}
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteNotification(notification)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bell className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Notifications
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gerez les notifications affichees aux utilisateurs
          </p>
        </div>

        {isAdmin && (
          <Button asChild>
            <Link to="/notifications/new">
              <Plus className="h-4 w-4" />
              Nouvelle notification
            </Link>
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="news">Nouveaute</SelectItem>
            <SelectItem value="trick">Astuce</SelectItem>
            <SelectItem value="warning">Alerte</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredAndSortedNotifications}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Rechercher par titre, message ou page..."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        totalItems={filteredAndSortedNotifications.length}
        emptyMessage="Aucune notification trouvee"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteNotification} onOpenChange={() => setDeleteNotification(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la notification ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la notification{' '}
              <strong>{deleteNotification?.name}</strong> ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
